import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { fmtDate } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { PerformanceRecomputeResponse } from '@/shared/api/types'

/**
 * Re-running the nightly rollup by hand.
 *
 * ## Why anybody needs this
 *
 * Scoring weights live in settings, so somebody will change them — and the rows
 * already written were computed under the old ones. Without this the answer to
 * "the weights changed, fix the history" is a database job. With it, it is an
 * afternoon.
 *
 * ## The window it actually recomputes is wider than the one you ask for
 *
 * A score is a trailing window as of its day, so recomputing day D also has to
 * recompute the days *after* D whose windows contained it. The server does that
 * and reports the `effectiveTo` it used; this panel prints that, not the `to`
 * that was asked for. Printing the request back would leave up to a month of
 * stale scores looking freshly computed.
 */
export function RecomputePanel({
  from, to, onDone, recompute,
}: {
  from: string
  to: string
  onDone: () => void
  recompute: (from: string, to: string) => Promise<PerformanceRecomputeResponse>
}) {
  // `mobilizer:score` — its own authority, held by neither VIEWER nor the seeded
  // MOBILIZER role. Rendering the panel without it would be a visible 403.
  const canScore = useCan('mobilizer:score')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<PerformanceRecomputeResponse | null>(null)

  if (!canScore) return null

  const run = async () => {
    setBusy(true); setError(null)
    try {
      setResult(await recompute(from, to))
      onDone()
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  return (
    <Panel
      title="Recompute scores"
      description="For when the weights changed and the history was computed under the old ones."
      right={(
        <button type="button" className="erp-btn" disabled={busy} onClick={() => void run()}>
          <RotateCcw className="h-3.5 w-3.5" />
          {busy ? 'Recomputing…' : 'Recompute this range'}
        </button>
      )}
    >
      <div className="space-y-2">
        <ErrorStrip message={error} />
        <p className="text-erp-sm text-muted-foreground">
          Recomputes {fmtDate(from)} to {fmtDate(to)}. Because a score is a trailing
          window, the days after the range are recomputed too — their windows
          contained these days.
        </p>
        {result && (
          <Note tone="good">
            {result.daysRecomputed} day{result.daysRecomputed === 1 ? '' : 's'} requested;
            scores were rewritten through {fmtDate(result.effectiveTo)} under rules
            v{result.rulesVersion}.
          </Note>
        )}
      </div>
    </Panel>
  )
}
